import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { GenericResponse } from '@app/core/models/generic-response';
import { DocType } from '@app/core/models/doc-type.model';
import { AuthStore } from '@app/core/state/auth.store';
import { UploadSessionDto, UploadSessionItem } from '@modules/reconciliation-manager/models';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TimezoneConverterService } from '@app/core/services/timezone-converter.service';
import { TenantStateService } from '@app/core/services/tenant-state.service';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { LoggingService } from '@app/core/services/logging.service';

export interface ProcessSessionResponse {
    jobId: string;
    sessionId: string;
    message: string;
    tenant_id?: string;
}

@Injectable({
    providedIn: 'root',
})
export class UpdateSessionService {
    private readonly loggingService = inject(LoggingService);
    constructor(
        private http: HttpClient,
        private authStore: AuthStore,
        private timezoneConverterService: TimezoneConverterService,
        private tenantStateService: TenantStateService,
        private appSettingsStore: AppSettingsStore
    ) {}

    /** Build common headers and ensure required values are present */
    private buildHeaders(): HttpHeaders {
        const userId = this.authStore.getUserValue();
        const clientId = this.authStore.getClientValue();

        if (!userId || !clientId) {
            throw new Error('Missing X-User or X-ClientId header values');
        }

        return new HttpHeaders({
            'X-User': userId,
            'X-ClientId': clientId,
            'X-Tenant-Id': this.tenantStateService.getCurrentTenantId(),
        });
    }

    /**
     * Increment file count on an existing upload session.
     */
    updateFileCount(
        sessionId: string,
        createdDate: Date | string,
        note: string
    ): Observable<GenericResponse<UploadSessionItem>> {
        const headers = this.buildHeaders();

        return this.timezoneConverterService.ensureUtcIsoString(createdDate).pipe(
            switchMap((utcDate) => {
                const params = new HttpParams()
                    .set('createdDate', utcDate)
                    .set('note', note ?? '');

                const url = `${environment.apiRoot}sessions/${encodeURIComponent(sessionId)}/processing/total-file-count`;

                return this.http
                    .put<GenericResponse<UploadSessionItem>>(url, {}, { headers, params })
                    .pipe(
                        map((response) => {
                            if (!response.success) {
                                throw new Error(response.message || 'Error updating file count.');
                            }
                            return response;
                        })
                    );
            }),
            catchError((error) => {
                this.loggingService.error('Failed to update session file count.', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Update the session summary and persist the selected document type.
     */
    updateUploadSessionSummary(
        session: UploadSessionItem,
        note: string,
        docType: DocType
    ): Observable<GenericResponse<UploadSessionItem>> {
        const headers = this.buildHeaders();

        const sessionId = session.id?.trim();
        if (!sessionId) {
            return throwError(() => new Error('Session ID is required to update the session summary.'));
        }

        const createdDate = session.createdDateUtc || session.createdDate;
        if (!createdDate) {
            return throwError(() => new Error('Created date is required to update the session summary.'));
        }

        return this.timezoneConverterService.ensureUtcIsoString(createdDate).pipe(
            switchMap((utcDate) => {
                const url = `${environment.apiRoot}sessions/${encodeURIComponent(sessionId)}`;
                const body = {
                    sessionId,
                    user: session.user || this.authStore.getUserValue() || '',
                    status: session.status || '',
                    createDate: utcDate,
                    createDateUtc: utcDate,
                    note: note ?? '',
                    docType,
                    userTimeZoneIana: this.resolveUserTimeZone(session),
                };

                return this.http.patch<GenericResponse<UploadSessionItem>>(url, body, { headers }).pipe(
                    map((response) => {
                        if (!response.success) {
                            throw new Error(response.message || 'Error updating session summary.');
                        }
                        return response;
                    })
                );
            }),
            catchError((error) => {
                this.loggingService.error('Failed to update session summary.', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Update the status (and optionally other metadata) of an active session.
     */
    updateSessionStatus(
        activeSession: UploadSessionItem,
        status: string
    ): Observable<GenericResponse<UploadSessionItem>> {
        const headers = this.buildHeaders();
        const dto = this.mapToUploadSessionDto(activeSession, status);
        const url = `${environment.apiRoot}sessions/${encodeURIComponent(activeSession.id)}/active`;

        return this.http.put<GenericResponse<UploadSessionItem>>(url, dto, { headers }).pipe(
            map((res) => {
                if (!res.success) {
                    throw new Error(res.message || 'Error updating session status.');
                }
                return res;
            }),
            catchError((err: HttpErrorResponse) => {
                this.loggingService.error('Failed to update session status.', err);
                return throwError(() => err);
            })
        );
    }

    /**
     * Map an UploadSessionItem + status into the shared DTO interface.
     */
    private mapToUploadSessionDto(session: UploadSessionItem, status: string): UploadSessionDto {
        const userId = this.authStore.getUserValue()!;
        const clientId = this.authStore.getClientValue()!;
        const resolvedTimeZone = this.resolveUserTimeZone(session);

        return {
            sessionId: session.id,
            user: userId,
            clientId,
            createDate: session.createdDate,
            createDateUtc: session.createdDateUtc || session.createdDate,
            note: session.notes ?? '',
            status,
            fileName: session.results ?? session.filename ?? undefined,
            totalFiles: session.total ?? undefined,
            completeDate: session.completeDate ?? undefined,
            completeDateUtc: session.completeDateUtc ?? session.completeDate ?? undefined,
            userTimeZoneIana: resolvedTimeZone,
            offsetMinutesAtUpload: session.offsetMinutesAtUpload,
        };
    }

    private resolveUserTimeZone(session: UploadSessionItem): string {
        const sessionZone = session.userTimeZoneIana?.trim();
        if (sessionZone && sessionZone.length > 0) {
            return sessionZone;
        }
        const uiZone = this.appSettingsStore.currentTimeZone();
        if (uiZone && uiZone.length > 0) {
            return uiZone;
        }
        return 'UTC';
    }

    /**
     * Trigger batch OCR processing for a session
     */
    processSession(
        sessionId: string,
        docType: DocType = DocType.Statement
    ): Observable<HttpResponse<ProcessSessionResponse>> {
        const headers = this.buildHeaders();
        const url = `${environment.apiRoot}sessions/${encodeURIComponent(sessionId)}/processing`;
        const normalizedDocType = this.normalizeDocType(docType);

        return this.http
            .post<ProcessSessionResponse>(
                url,
                { sessionId, docType: normalizedDocType },
                {
                    headers,
                    observe: 'response',
                }
            )
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    this.loggingService.error('Failed to start session processing.', err);
                    return throwError(() => err);
                })
            );
    }

    private normalizeDocType(value: DocType): DocType {
        return value === DocType.Invoice ? DocType.Invoice : DocType.Statement;
    }
}
